import { useCallback, useContext, useEffect, useState } from 'react';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { Content } from '@ftrprf/tailwind-components';
import { ReactComponent as LoaderBars } from 'assets/vectors/logo-animated-lines.svg';
import Abducted from 'assets/vectors/error-pages/abducted.svg';
import useAnswerQuestion from 'hooks/api/portfolioService/mutations/v2/useAnswerQuestion';
import useFindSavedAnswers from 'hooks/api/portfolioService/queries/useSavedAnswers';
import useLessonContent from 'hooks/api/slidesService/useLessonContent';
import useFormatMessage from 'hooks/useFormatMessage';
import { Error403 } from 'pages/Error/Error403';
import { LocationHistoryContext } from 'providers/LocationHistoryProvider';
import { UserContext } from 'providers/UserProvider';
import { SLIDE_VIEWER_SIDEBAR_OPEN } from 'utils/constants/localStorage';
import URLS from 'utils/constants/urls';
import SlideViewerError from './partials/SlideViewerError';
import { SlideViewer } from './SlideViewer';
import {
  generateCurrentLessonPath,
  generateLessonPath,
  viewModeToLowerCase,
  viewModeToUpperCase,
} from './utils/helpers';
import { lessonsByStudioId } from '../../mocks/lessons/lessonsByStudioId';

export default function SlideViewerContainer() {
  const navigate = useNavigate();
  const t = useFormatMessage();

  const { pathname } = useLocation();
  const { hasHistory, locationHistory } = useContext(LocationHistoryContext);
  const { organization } = useContext(UserContext);
  const { isCodefever } = organization;

  const {
    sessionId,
    slideId: urlSlideId,
    studioId,
    viewMode: urlViewMode,
  } = useParams();

  const slides = lessonsByStudioId[studioId];

  const [viewMode, setViewMode] = useState(viewModeToUpperCase(urlViewMode));
  const [slideId, setSlideId] = useState(urlSlideId);
  const [answers, setAnswers] = useState([]);

  useEffect(() => {
    if (slideId !== urlSlideId) {
      setSlideId(urlSlideId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSlideId]);

  // won't fetch lesson if no sessionId => if teacher is previewing
  const { data: lesson, isLoading: isLoadingLesson } = useLessonContent(
    studioId,
    sessionId,
  );

  const error = false;
  const isLoadingSlides = false;

  const { isLoading: isSubmittingAnswer, mutateAsync: saveAnswer } =
    useAnswerQuestion();

  useEffect(() => {
    if (!slideId && slides && slides.length !== 0) {
      setSlideId(slides[0].id);
    }
  }, [slideId, slides, setSlideId]);

  useEffect(() => {
    if (slides && slides.length !== 0) {
      if (urlSlideId) {
        /* --- take a seat and some rest, that's life at its best ---*/
        /*
          when the urlSlideId is present, everything needed is present
        */
      } else if (!urlSlideId) {
        navigate(
          generatePath(URLS.SLIDEVIEWER_OVERVIEW_SLIDE_VIEWMODE_SLIDEID, {
            slideId: slideId ?? slides[0]?.id,
            studioId,
            viewMode: viewModeToLowerCase(viewMode),
          }),
          { replace: true },
        );
      } else {
        navigate(
          generatePath(URLS.SLIDEVIEWER_OVERVIEW_VIEWMODE, {
            studioId,
            viewMode: viewModeToLowerCase(viewMode),
          }),
          { replace: true },
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideId, slides, viewMode]);

  const onChangeViewMode = useCallback(
    (newViewMode) => {
      if (newViewMode && newViewMode !== viewMode) {
        setViewMode(newViewMode);
      }
    },
    [viewMode],
  );

  const onChangeSlide = useCallback(
    (newSlideId) => {
      if (newSlideId && newSlideId !== slideId) {
        setSlideId(newSlideId);
        navigate(
          generatePath(URLS.SLIDEVIEWER_OVERVIEW_SLIDE_VIEWMODE_SLIDEID, {
            slideId: newSlideId,
            studioId,
            viewMode: viewModeToLowerCase(viewMode),
          }),
        );
      }
    },
    [navigate, slideId, studioId, viewMode],
  );

  const { data: savedAnswers, refetch: refetchSavedAnswers } =
    useFindSavedAnswers(studioId);

  useEffect(() => {
    if (savedAnswers) {
      setAnswers(savedAnswers);
    }
  }, [savedAnswers]);

  const onSubmitQuestionAnswers = async (question, values) => {
    const slide = slides.find(({ id }) => id === slideId);
    const data = await saveAnswer(
      {
        answer: values,
        questionId: question.id,
        questionType: question.type,
        studioId,
      },
      slide.questions?.[0]?.questionAnswersMultipleChoice,
    );

    if (Object.values(data)[0]) {
      setAnswers([...answers, Object.values(data)[0]]);
    } else {
      setAnswers([
        ...answers,
        {
          questionId: question.id,
          questionType: question.type,
          userAnswer: values,
        },
      ]);
    }

    return Object.values(data)[0];
  };

  const onClose = useCallback(() => {
    if (hasHistory) {
      navigate(
        locationHistory.find(
          (val) =>
            // this beautiful piece of regex returns all sections of a pathname:
            // e.g.: "/studio/1/overview/slide/1"
            // would return ["/studio", "/1", "/overview", "/slide", "/1"]
            // and then we just want the first one that's different from the current pathname (current path: /slideviewer), e.g.: /home or /learn
            val.match(/^\/([^/])*/)[0] !== pathname.match(/^\/([^/])*/)[0],
        ),
        // when we have retrieved the right URL from our history, we can navigate out of slideviewer, to the first page that isn't part of slideviewer
      );
    } else {
      navigate(generatePath(URLS.ROOT));
    }
  }, [hasHistory, locationHistory, navigate, pathname]);

  useEffect(() => {
    localStorage.setItem(SLIDE_VIEWER_SIDEBAR_OPEN, false);
  }, []);

  useEffect(() => {
    if (sessionId) {
      refetchSavedAnswers();
    }
  }, [sessionId, refetchSavedAnswers]);

  if (slides?.length === 0) {
    return <SlideViewerError onClose={onClose} />;
  }

  if (error && JSON.stringify(error).includes('FORBIDDEN')) {
    return (
      <Error403
        message={<span className="text-center">{t('errors.403.message')}</span>}
      />
    );
  }

  return isLoadingLesson || isLoadingSlides ? (
    <Content hasPaddingBottom={false}>
      <div className="p-4 w-full flex flex-col justify-center items-center">
        <img alt="" className="inset-0 object-cover py-4 w-64" src={Abducted} />
        <LoaderBars className="w-32 p-8" />
      </div>
    </Content>
  ) : (
    <SlideViewer
      generateCurrentLessonPath={({ slideId }) =>
        generateCurrentLessonPath({ slideId, studioId, viewMode })
      }
      generateLessonPath={({ isHackRoomLesson, slideId, studioId }) =>
        generateLessonPath({
          isHackRoomLesson,
          slideId,
          studioId,
          viewMode,
        })
      }
      isCodefever={isCodefever}
      isLoadingLesson={isLoadingLesson}
      isLoadingSlides={isLoadingSlides}
      isSubmittingAnswers={isSubmittingAnswer}
      lesson={lesson}
      lessonSessionId={sessionId}
      onChangeSlide={onChangeSlide}
      onChangeViewMode={onChangeViewMode}
      onClose={onClose}
      onSubmitQuestionAnswers={onSubmitQuestionAnswers}
      refetchAnswers={refetchSavedAnswers}
      selectedPeriodIsNotActivePeriod={false}
      setViewMode={setViewMode}
      slideId={slideId}
      slides={slides}
      studioId={studioId}
      submittedQuestionAnswers={answers}
      viewMode={viewMode}
    />
  );
}
